/**
 * Create Class Dialog
 * Form for creating a new training class
 */

import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Alert, AlertDescription } from '../ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ClassCodeGenerator } from './ClassCodeGenerator';
import { AlertCircle } from 'lucide-react';
import { useEnvironments } from '../../hooks/useEnvironments';
import type { Class } from '@cloak-next/shared';

interface CreateClassDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreateClass: (data: CreateClassData) => Promise<void>;
  existingClasses: Class[]; // For shortClassId uniqueness check
}

export interface CreateClassData {
  className: string;
  shortClassId: string;
  startTime: string;
  endTime: string;
  maxStudents: number;
  classCode?: string;
  isDraft?: boolean;
  environmentId: string;
}

export function CreateClassDialog({
  open,
  onOpenChange,
  onCreateClass,
  existingClasses,
}: CreateClassDialogProps) {
  const {
    environments,
    loading: environmentsLoading,
    error: environmentsError,
    fetchEnvironments,
  } = useEnvironments();

  const [formData, setFormData] = useState<CreateClassData>({
    className: '',
    shortClassId: '',
    startTime: '',
    endTime: '',
    maxStudents: 20,
    classCode: '',
    isDraft: true,
    environmentId: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get the selected environment for description display
  const selectedEnvironment = environments.find((e) => e.id === formData.environmentId);

  // Fetch environments when dialog opens
  useEffect(() => {
    if (open) {
      fetchEnvironments().then((loadedEnvironments) => {
        // Pre-select first environment if none selected
        if (loadedEnvironments.length > 0 && !formData.environmentId) {
          setFormData((prev) => ({ ...prev, environmentId: loadedEnvironments[0].id }));
        }
      });
    }
  }, [open, fetchEnvironments]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setFormData({
        className: '',
        shortClassId: '',
        startTime: '',
        endTime: '',
        maxStudents: 20,
        classCode: '',
        isDraft: true,
        environmentId: '',
      });
      setErrors({});
    }
  }, [open]);

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Environment is required
    if (!formData.environmentId) {
      newErrors.environmentId = 'Please select a lab environment';
    }

    // Required fields
    if (!formData.className.trim()) {
      newErrors.className = 'Class name is required';
    }

    if (!formData.shortClassId.trim()) {
      newErrors.shortClassId = 'Short class ID is required';
    } else {
      // Check uniqueness (client-side)
      const shortIdLower = formData.shortClassId.toLowerCase();
      const isDuplicate = existingClasses.some(
        (c) => c.shortClassId.toLowerCase() === shortIdLower
      );
      if (isDuplicate) {
        newErrors.shortClassId = 'This short class ID is already in use';
      }

      // Format validation (alphanumeric, lowercase, no spaces)
      if (!/^[a-z0-9-]+$/.test(formData.shortClassId)) {
        newErrors.shortClassId =
          'Only lowercase letters, numbers, and hyphens allowed';
      }
    }

    if (!formData.startTime) {
      newErrors.startTime = 'Start time is required';
    }

    if (!formData.endTime) {
      newErrors.endTime = 'End time is required';
    }

    // Date validation
    if (formData.startTime && formData.endTime) {
      const start = new Date(formData.startTime);
      const end = new Date(formData.endTime);

      if (end <= start) {
        newErrors.endTime = 'End time must be after start time';
      }
    }

    if (formData.maxStudents < 1 || formData.maxStudents > 100) {
      newErrors.maxStudents = 'Maximum students must be between 1 and 100';
    }

    // Class code validation (if provided)
    if (formData.classCode) {
      if (formData.classCode.length < 6 || formData.classCode.length > 12) {
        newErrors.classCode = 'Class code must be 6-12 characters';
      }
      if (!/^[A-Z0-9]+$/.test(formData.classCode)) {
        newErrors.classCode = 'Class code must be uppercase alphanumeric';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onCreateClass({
        ...formData,
        startTime: new Date(formData.startTime).toISOString(),
        endTime: new Date(formData.endTime).toISOString(),
      });
      onOpenChange(false);
    } catch (error: any) {
      // Show API errors
      setErrors({
        submit: error.message || 'Failed to create class',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Class</DialogTitle>
          <DialogDescription>
            Set up a new training class. Save as draft to finalize details later, or
            publish to enable student enrollment immediately.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Submit Error */}
          {errors.submit && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{errors.submit}</AlertDescription>
            </Alert>
          )}

          {/* Basic Info */}
          <div className="space-y-4">
            <h3 className="font-semibold">Basic Information</h3>

            <div className="space-y-2">
              <Label htmlFor="className">
                Class Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="className"
                value={formData.className}
                onChange={(e) =>
                  setFormData({ ...formData, className: e.target.value })
                }
                placeholder="e.g., Web Security 101 - Spring 2025"
              />
              {errors.className && (
                <p className="text-sm text-destructive">{errors.className}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="shortClassId">
                Short Class ID <span className="text-destructive">*</span>
              </Label>
              <Input
                id="shortClassId"
                value={formData.shortClassId}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    shortClassId: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''),
                  })
                }
                placeholder="e.g., websec101"
                className="font-mono"
              />
              {errors.shortClassId && (
                <p className="text-sm text-destructive">{errors.shortClassId}</p>
              )}
              <p className="text-xs text-muted-foreground">
                A short identifier for this class (used for resource tagging and reporting)
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startTime">
                  Start Time <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="startTime"
                  type="datetime-local"
                  value={formData.startTime}
                  onChange={(e) =>
                    setFormData({ ...formData, startTime: e.target.value })
                  }
                />
                {errors.startTime && (
                  <p className="text-sm text-destructive">{errors.startTime}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="endTime">
                  End Time <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="endTime"
                  type="datetime-local"
                  value={formData.endTime}
                  onChange={(e) =>
                    setFormData({ ...formData, endTime: e.target.value })
                  }
                />
                {errors.endTime && (
                  <p className="text-sm text-destructive">{errors.endTime}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="maxStudents">
                Maximum Students <span className="text-destructive">*</span>
              </Label>
              <Input
                id="maxStudents"
                type="number"
                min={1}
                max={100}
                value={formData.maxStudents}
                onChange={(e) =>
                  setFormData({ ...formData, maxStudents: parseInt(e.target.value) || 20 })
                }
              />
              {errors.maxStudents && (
                <p className="text-sm text-destructive">{errors.maxStudents}</p>
              )}
              <p className="text-xs text-muted-foreground">
                Maximum number of students who can enroll in this class
              </p>
            </div>
          </div>

          {/* Lab Environment */}
          <div className="space-y-4">
            <h3 className="font-semibold">Lab Environment</h3>
            {environmentsError && (
              <p className="text-sm text-destructive">{environmentsError}</p>
            )}
            <Select
              value={formData.environmentId}
              onValueChange={(v) => setFormData({ ...formData, environmentId: v })}
              disabled={environmentsLoading}
            >
              <SelectTrigger>
                <SelectValue placeholder={environmentsLoading ? 'Loading environments...' : 'Select lab environment'} />
              </SelectTrigger>
              <SelectContent>
                {environments.map((env) => (
                  <SelectItem key={env.id} value={env.id}>
                    {env.name} — ~${env.estimatedCostPerHour.toFixed(2)}/hr
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedEnvironment && (
              <p className="text-sm text-muted-foreground">{selectedEnvironment.description}</p>
            )}
            {errors.environmentId && (
              <p className="text-sm text-destructive">{errors.environmentId}</p>
            )}
          </div>

          {/* Enrollment Code */}
          <div className="space-y-4">
            <h3 className="font-semibold">Enrollment Settings</h3>
            <ClassCodeGenerator
              classCode={formData.classCode || ''}
              onClassCodeChange={(classCode) =>
                setFormData({ ...formData, classCode })
              }
            />
            {errors.classCode && (
              <p className="text-sm text-destructive">{errors.classCode}</p>
            )}
          </div>

          {/* Draft Mode */}
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <Label htmlFor="isDraft" className="text-base font-medium">
                Save as Draft
              </Label>
              <p className="text-sm text-muted-foreground">
                {formData.isDraft
                  ? 'Class will be created as draft (students cannot enroll yet)'
                  : 'Class will be published immediately (students can enroll with code)'}
              </p>
            </div>
            <Switch
              id="isDraft"
              checked={formData.isDraft}
              onCheckedChange={(checked) =>
                setFormData({ ...formData, isDraft: checked })
              }
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting
                ? 'Creating...'
                : formData.isDraft
                  ? 'Create Draft'
                  : 'Create & Publish'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
